// MigrationTab.jsx — the project's Migration surface (FMX restyle, step 4).
//
// Replaces the old SchemaOverview card grid + WorkspaceSidebar with a single
// FMX data grid: a colored progress header on top, then the entity types
// grouped into category band rows in IMPORT_ORDER. Status, rows, and last
// import derive from the newest import record per entity: Complete (all rows
// pushed), Partial push (some/all rows failed), or Mapped — not pushed
// (saved/downloaded only). The header meter counts "has at least one saved
// import" so it keeps matching the Projects-list denominator exactly.

import { useMemo } from 'react';
import { T } from '../theme';
import { getImportOrder, getBaseSchemaType, getSchemaDisplayName, getSchemaModuleSlug, isModuleDisabled } from '../schemas';
import { normalizeModules } from '../fmxSync';
import { importRowsCount, importPushState } from '../utils';
import { Button, Badge, Meter, IconButton, FilterChip } from './fmxkit';

// Category grouping for the flat import order (mirrors the retired WorkspaceSidebar).
const CATEGORY_RULES = [
  { key: 'core', label: 'Core Data', match: (s) => ['Building', 'Resource', 'User', 'Equipment Type', 'Equipment', 'Inventory'].includes(s) },
  { key: 'work_requests', label: 'Work Requests', match: (s) => getBaseSchemaType(s) === 'Work Request' },
  { key: 'schedule_requests', label: 'Schedule Requests', match: (s) => getBaseSchemaType(s) === 'Schedule Request' },
  { key: 'work_tasks', label: 'Work Tasks', match: (s) => getBaseSchemaType(s) === 'Work Task' },
  { key: 'other', label: 'Other', match: () => true },
];

// Dependency text keyed by base schema type (mirrors the retired SchemaOverview).
const BASE_DEPS = {
  Building:                 'No dependencies',
  Resource:                 'Requires: Building',
  User:                     'Requires: Building',
  'Equipment Type':         'No dependencies',
  Equipment:                'Requires: Building + Equipment Type',
  Inventory:                'Requires: Building',
  'Work Request':           'Requires: Building (Equipment optional)',
  'Schedule Request':       'Requires: Building (Resource optional)',
  'Work Task':              'Requires: Building (Equipment optional)',
  'Transportation Request': 'Requires: Building (Resource optional)',
  'Accounting Account':     'No dependencies',
};

const STATUS_BADGE = {
  done:    { color: 'green',     label: 'Complete',           icon: 'circle-check' },
  partial: { color: 'orange',    label: 'Partial push',       icon: 'circle-exclamation' },
  mapped:  { color: 'blueLight', label: 'Mapped — not pushed', icon: 'file-arrow-down' },
  active:  { color: 'blueDark',  label: 'In progress',        icon: 'spinner' },
  ready:   { color: 'gray',      label: 'Not started',        icon: null },
};

// Cycle order for the status filter chip.
const STATUS_CYCLE = ['all', 'done', 'partial', 'mapped', 'active', 'ready'];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatusToken({ status, n }) {
  const map = {
    done:    { bg: T.green500,   fg: T.white,    content: <i className="fa-solid fa-check" style={{ fontSize: 11 }} /> },
    partial: { bg: T.orange500,  fg: T.white,    content: <i className="fa-solid fa-exclamation" style={{ fontSize: 11 }} /> },
    mapped:  { bg: T.blue100,    fg: T.inkMuted, content: <i className="fa-solid fa-file-arrow-down" style={{ fontSize: 11 }} /> },
    active:  { bg: T.accentOrange, fg: T.white,  content: String(n).padStart(2, '0') },
    ready:   { bg: T.blue50,     fg: T.inkMuted, content: String(n).padStart(2, '0') },
  };
  const s = map[status] || map.ready;
  return (
    <span style={{
      width: 26, height: 26, borderRadius: 6, background: s.bg, color: s.fg,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      font: `700 12px ${T.fontData}`, flexShrink: 0,
      border: status === 'ready' ? `1px solid ${T.blue100}` : 'none',
    }}>{s.content}</span>
  );
}

function DepText({ deps }) {
  if (!deps || deps === 'No dependencies') return <span style={{ color: T.inkSoft }}>No dependencies</span>;
  return (
    <span style={{ color: T.inkMuted }}>
      <i className="fa-solid fa-arrow-turn-up fa-rotate-90" style={{ color: T.cyan, fontSize: 10, marginRight: 6 }} />
      {deps.replace('Requires: ', '')}
    </span>
  );
}

export default function MigrationTab({
  project, imports = [], cardSettings = {}, activeWizardSchema = null,
  canDataImport = true, hasCreds = false, onUpload, onResumeWizard, onView, onRepush, onOpenOcr,
  statusFilter = 'all', onStatusFilter, categoryFilter = 'all', onCategoryFilter,
}) {
  const mods = useMemo(() => normalizeModules(project?.fmx_modules), [project?.fmx_modules]);
  const importOrder = useMemo(() => getImportOrder(mods), [mods]);

  const bySchema = useMemo(() => {
    const map = {};
    for (const imp of imports) (map[imp.schema_type] || (map[imp.schema_type] = [])).push(imp);
    return map;
  }, [imports]);

  // Applicable = not hidden via card settings. (Disabled modules are shown greyed.)
  const applicable = useMemo(
    () => importOrder.filter(s => !cardSettings[s]?.hidden),
    [importOrder, cardSettings]
  );

  const rowOf = (schema) => {
    const recs = bySchema[schema] || [];
    const done = recs.length > 0; // imported = has an import (agrees with Projects list)
    // Badge + rows come from the newest record only, so re-pushes replace the
    // previous outcome instead of double-counting.
    let latest = null;
    for (const r of recs) {
      if (!latest || new Date(r.completed_at || 0) > new Date(latest.completed_at || 0)) latest = r;
    }
    const status = done
      ? (importPushState(latest) === 'pushed' ? 'done' : importPushState(latest))
      : (schema === activeWizardSchema ? 'active' : 'ready');
    const rows = importRowsCount(latest);
    const last = latest?.completed_at ? { when: latest.completed_at, file: latest.source_filename } : null;
    return { schema, recs, done, status, rows, last, disabled: isModuleDisabled(schema, mods) };
  };

  const rows = useMemo(() => applicable.map(rowOf), [applicable, bySchema, activeWizardSchema, mods]); // eslint-disable-line

  // importedCount (has ≥1 saved import) drives the meter/denominator so it
  // keeps agreeing with the Projects list; the finer statuses split the legend.
  const importedCount = rows.filter(r => r.done).length;
  const doneCount = rows.filter(r => r.status === 'done').length;
  const partialCount = rows.filter(r => r.status === 'partial').length;
  const mappedCount = rows.filter(r => r.status === 'mapped').length;
  const activeCount = rows.filter(r => r.status === 'active').length;
  const total = rows.length;
  const readyCount = total - importedCount - activeCount;
  const pct = total > 0 ? Math.round((importedCount / total) * 100) : 0;
  const rowsPushed = rows.reduce((sum, r) => sum + r.rows, 0);
  const nextEntity = rows.find(r => !r.done);
  const nextLabel = nextEntity ? getBaseSchemaType(nextEntity.schema) : null;

  // Group applicable rows by category, preserving order.
  const grouped = useMemo(() => {
    return CATEGORY_RULES.map(cat => ({
      cat,
      items: rows.filter(r => CATEGORY_RULES.find(c => c.match(r.schema)) === cat),
    })).filter(g => g.items.length > 0);
  }, [rows]);

  const matchesFilters = (r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && CATEGORY_RULES.find(c => c.match(r.schema))?.key !== categoryFilter) return false;
    return true;
  };

  const renderAction = (r) => {
    if (r.disabled) return <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft, fontStyle: 'italic' }}>Module disabled</span>;
    if (r.done) return (
      <>
        <Button variant="tertiary" size="sm" icon="eye" onClick={() => onView && onView(r.recs[0])}>View</Button>
        {hasCreds && canDataImport && onRepush && (
          <Button variant="tertiary" size="sm" icon="cloud-arrow-up" onClick={() => onRepush(r.recs[0])}>Re-push</Button>
        )}
      </>
    );
    if (!canDataImport) return null;
    if (r.status === 'active') return <Button variant="primary" size="sm" icon="play" onClick={() => onResumeWizard && onResumeWizard()}>Resume</Button>;
    return <Button variant="primary" size="sm" icon="arrow-up-from-bracket" onClick={() => onUpload && onUpload(r.schema)}>Upload</Button>;
  };

  const hs = (w, right) => ({ textAlign: right ? 'right' : 'left', font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '9px 14px', background: T.bgMuted, borderBottom: `1px solid ${T.borderSubtle}`, width: w, whiteSpace: 'nowrap' });
  const cell = { padding: '10px 14px', borderBottom: `1px solid ${T.borderSubtle}`, font: `400 14px/20px ${T.fontSans}`, color: T.ink, verticalAlign: 'middle' };

  const legend = [
    { c: T.green500, n: doneCount, l: 'complete' },
    ...(partialCount > 0 ? [{ c: T.orange500, n: partialCount, l: 'partial push' }] : []),
    ...(mappedCount > 0 ? [{ c: T.blue100, n: mappedCount, l: 'mapped only' }] : []),
    { c: T.accentOrange, n: activeCount, l: 'in progress' },
    { c: 'transparent', border: T.border, n: readyCount, l: 'not started' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Progress header */}
      <div style={{ background: T.bgMuted, borderBottom: `1px solid ${T.borderSubtle}`, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ font: `800 40px/38px ${T.fontData}`, color: T.navy, letterSpacing: '-0.03em' }}>{importedCount}</span>
          <span style={{ font: `700 22px ${T.fontSans}`, color: T.cyan }}>/</span>
          <span style={{ font: `700 24px ${T.fontData}`, color: T.inkSoft }}>{total}</span>
          <span style={{ font: `600 13px ${T.fontSans}`, color: T.inkMuted, marginLeft: 8 }}>entity types<br />migrated</span>
        </div>
        <div style={{ flex: 1, minWidth: 260, maxWidth: 460 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', font: `600 12px ${T.fontSans}`, color: T.inkMuted, marginBottom: 6 }}>
            <span>{pct}% complete</span>
            {nextLabel && <span>Next: <b style={{ color: T.ink }}>{nextLabel}</b></span>}
          </div>
          <Meter pct={pct} h={9} />
          <div style={{ display: 'flex', gap: 16, marginTop: 9 }}>
            {legend.map((g, i) => (
              <span key={i} style={{ font: `500 12px ${T.fontSans}`, color: T.inkMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: g.c, border: g.border ? `1.5px solid ${g.border}` : 'none', boxSizing: 'border-box' }} />
                {g.n} {g.l}
              </span>
            ))}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ background: T.white, border: `1px solid ${T.blue200}`, borderRadius: T.radiusCard, padding: '10px 16px', textAlign: 'right' }}>
            <div style={{ font: `800 20px ${T.fontData}`, color: T.brand }}>{rowsPushed.toLocaleString()}</div>
            <div style={{ font: `600 11px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>rows pushed</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '12px 24px 0', background: T.bg, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="neutral" size="sm" icon="filter" onClick={() => { onStatusFilter && onStatusFilter('all'); onCategoryFilter && onCategoryFilter('all'); }}>Filters</Button>
        <FilterChip icon="circle-check" label="Status" value={statusFilter === 'all' ? 'All' : (STATUS_BADGE[statusFilter]?.label || 'All')}
          onClick={() => {
            const idx = STATUS_CYCLE.indexOf(statusFilter);
            onStatusFilter && onStatusFilter(STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]);
          }} />
        <FilterChip icon="layer-group" label="Category" value={categoryFilter === 'all' ? 'All' : (CATEGORY_RULES.find(c => c.key === categoryFilter)?.label || 'All')}
          onClick={() => {
            const keys = ['all', ...CATEGORY_RULES.map(c => c.key)];
            const idx = keys.indexOf(categoryFilter);
            onCategoryFilter && onCategoryFilter(keys[(idx + 1) % keys.length]);
          }} />
        <div style={{ flex: 1 }} />
        <span style={{ font: `400 13px/20px ${T.fontSans}`, color: T.inkMuted }}>{importedCount} imported · {readyCount + activeCount} remaining</span>
      </div>

      {/* Entity grid */}
      <div style={{ padding: '14px 24px 24px', background: T.bg, flex: 1, overflow: 'auto' }}>
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={hs(54)}>Step</th>
                <th style={hs()}>Entity type</th>
                <th style={hs(216)}>Dependencies</th>
                <th style={hs(140)}>Status</th>
                <th style={hs(96, true)}>Rows</th>
                <th style={hs(184)}>Last import</th>
                <th style={hs(150)} />
              </tr>
            </thead>
            <tbody>
              {grouped.flatMap(({ cat, items }) => {
                const visible = items.filter(matchesFilters);
                if (visible.length === 0) return [];
                const catDone = items.filter(r => r.done).length;
                const band = (
                  <tr key={`band-${cat.key}`}>
                    <td colSpan={7} style={{ background: T.blue50, padding: '7px 14px', borderBottom: `1px solid ${T.borderSubtle}` }}>
                      <span style={{ font: `700 11px/14px ${T.fontSans}`, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.brand }}>{cat.label}</span>
                      <span style={{ font: `600 11px ${T.fontSans}`, color: T.inkSoft, marginLeft: 8 }}>{catDone}/{items.length}</span>
                    </td>
                  </tr>
                );
                const itemRows = visible.map((r, idx) => {
                  const sb = STATUS_BADGE[r.status];
                  const stepNum = applicable.indexOf(r.schema) + 1;
                  const isEquipment = getBaseSchemaType(r.schema) === 'Equipment' && !getSchemaModuleSlug(r.schema);
                  const rowBg = r.status === 'active' ? 'rgba(206,74,18,0.035)' : T.white;
                  return (
                    <tr key={r.schema}
                      style={{ background: rowBg, cursor: r.disabled ? 'default' : 'pointer', opacity: r.disabled ? 0.6 : 1 }}
                      onClick={() => { if (!r.disabled) (r.done ? onView && onView(r.recs[0]) : r.status === 'active' ? onResumeWizard && onResumeWizard() : canDataImport && onUpload && onUpload(r.schema)); }}
                      onMouseEnter={e => { if (r.status !== 'active' && !r.disabled) e.currentTarget.style.background = T.bgMuted; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                      <td style={cell}><StatusToken status={r.status} n={stepNum} /></td>
                      <td style={cell}><span style={{ fontWeight: 600, color: r.done ? T.inkMuted : T.ink }}>{getSchemaDisplayName(r.schema)}</span></td>
                      <td style={{ ...cell, fontSize: 13 }}><DepText deps={BASE_DEPS[getBaseSchemaType(r.schema)]} /></td>
                      <td style={cell}><Badge color={sb.color} icon={sb.icon}>{sb.label}</Badge></td>
                      <td style={{ ...cell, textAlign: 'right', fontFamily: T.fontData, fontWeight: 600, color: r.rows ? T.ink : T.border }}>{r.rows ? r.rows.toLocaleString() : '—'}</td>
                      <td style={cell}>
                        {r.last
                          ? <div><div style={{ color: T.ink, fontSize: 13 }}>{r.last.file || getSchemaDisplayName(r.schema)}</div><div style={{ font: `400 12px/15px ${T.fontSans}`, color: T.inkSoft }}>{r.recs.length} import{r.recs.length > 1 ? 's' : ''} · {fmtDate(r.last.when)}</div></div>
                          : <span style={{ color: T.inkSoft, fontStyle: 'italic', fontSize: 13 }}>No imports yet</span>}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                          {renderAction(r)}
                          {isEquipment && onOpenOcr && <IconButton icon="camera" title="Existing Attachment Field Scan" theme="brand" onClick={onOpenOcr} />}
                        </span>
                      </td>
                    </tr>
                  );
                });
                return [band, ...itemRows];
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px', font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, flexWrap: 'wrap', gap: 8 }}>
          <span>Showing all {total} applicable entity types · order follows FMX import dependencies</span>
          {project?.fmx_site_url && <span>{rowsPushed.toLocaleString()} rows pushed to {project.fmx_site_url}</span>}
        </div>
      </div>
    </div>
  );
}
